import type { BatchProcessDto } from "@fp/admin-api-client";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { ProgressBar } from "@fp/ui/components/base/progress-indicators/progress-indicators";
import {
  getBatchAreaLabel,
  getBatchItemStatusMeta,
  getProgressPercentage,
  getSummaryStatusCounts,
} from "./batch-process-status";
import { getBatchDisplayName } from "./fsm-execute-sync-metadata";
import { FsmExecuteSyncWindow } from "./fsm-execute-sync-window";

export function BatchProcessSummary({ batch }: { batch: BatchProcessDto }) {
  const percentage = getProgressPercentage(batch);
  const counts = getSummaryStatusCounts(batch);

  const countWidth = `${String(batch.totalCount).length}ch`;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="min-w-0 pr-6">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-primary">
            {getBatchDisplayName(batch)}
          </span>
          <Badge size="sm" color="gray">
            {getBatchAreaLabel(batch.area)}
          </Badge>
        </div>
        <p className="mt-0.5 text-xs text-tertiary">
          {formatLocalDate(batch.createdDate, "datetimeWithYear")}
          {batch.createdByName && ` · ${batch.createdByName}`}
        </p>
        {batch.description && (
          <p className="mt-0.5 truncate text-xs text-tertiary">
            {batch.description}
          </p>
        )}
        <FsmExecuteSyncWindow batch={batch} />
      </div>

      <ProgressBar value={percentage} labelPosition="right" />

      <div className="flex flex-wrap items-center gap-1.5">
        {counts.map((entry) => {
          const statusMeta = getBatchItemStatusMeta(
            entry.status,
            batch.operation,
          );

          return (
            <Badge
              key={entry.status}
              size="sm"
              color={statusMeta.color}
              className="gap-1"
            >
              <span
                className="inline-block text-right tabular-nums"
                style={{ minWidth: countWidth }}
              >
                {entry.count}
              </span>
              {statusMeta.label}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}
